// components/LocationSelector.jsx
// 📍 Updated with onLocationChange callback

import React, { useState, useEffect } from 'react';
import { Select, Button, Modal, Form, Input, message, Tag } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSwitchLocationMutation, useAddLocationMutation, useFetchMyLocationsQuery } from '../pages/restaurant/restaruant.service';
import { setUser } from '../app.slice';
import { ROLE } from '../constant';

const LocationSelector = ({ onLocationChange }) => { // ✅ ADD THIS PROP
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.app.user);
  const [locations, setLocations] = useState([]);
  const [activeLocation, setActiveLocation] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const isOwner =
    user?.role === ROLE.RESTAURANT_ADMIN || user?.role === ROLE.SUPER_ADMIN;
  
  const [switchLocation, { isLoading: isSwitching }] = useSwitchLocationMutation();
  const [addLocation, { isLoading: isAdding }] = useAddLocationMutation();
  
  // ✅ Fetch locations (owners with multi-site only)
  const { data: locationsData, refetch } = useFetchMyLocationsQuery(undefined, {
    skip: !isOwner,
  });

  useEffect(() => {
    if (locationsData?.data?.locations) {
      setLocations(locationsData.data.locations);
      setActiveLocation(locationsData.data.activeLocation);
    }
  }, [locationsData]);

  // Managers / members / single-site: no switcher
  if (!isOwner || !locations || locations.length <= 1) {
    return null;
  }

  const handleSwitchLocation = async (locationId) => {
    try {
      const result = await switchLocation({ locationId }).unwrap();
      
      // ✅ Update token in localStorage
      if (result.data.token) {
        localStorage.setItem('token', result.data.token);
      }
      
      // ✅ Update user in Redux
      if (result.data.user) {
        dispatch(setUser(result.data.user));
      }
      
      setActiveLocation(locationId);
      message.success('Location switched successfully');
      
      // ✅ Call callback if provided
      if (onLocationChange) {
        onLocationChange(locationId);
      }
      
      // ✅ Reload page to refresh data
      window.location.reload();
    } catch (error) {
      message.error('Failed to switch location');
    }
  };

  const handleAddLocation = async (values) => {
    try {
      await addLocation(values).unwrap();
      message.success('New location added successfully');
      setIsModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error) {
      if (error.data?.message) {
        message.error(error.data.message);
      } else {
        message.error('Failed to add location');
      }
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Select
          value={activeLocation}
          onChange={handleSwitchLocation}
          loading={isSwitching}
          style={{ width: 400 }}
          placeholder={t("Select Location")}
        >
          {locations.map(loc => (
            <Select.Option key={loc._id} value={loc._id}>
              {loc.name}
              {loc.isHolidayMode && (
                <Tag color="orange" style={{ marginLeft: 8 }}>Holiday</Tag>
              )}
            </Select.Option>
          ))}
        </Select>
        
        {/* <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => setIsModalVisible(true)}
        >
          Add
        </Button> */}
      </div>

      {/* ✅ Add Location Modal */}
      <Modal
        title="Add New Location"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} onFinish={handleAddLocation} layout="vertical">
          <Form.Item
            name="name"
            label="Location Name"
            rules={[{ required: true, message: 'Please enter location name' }]}
          >
            <Input placeholder="e.g., London Branch" />
          </Form.Item>

          <Form.Item
            name="address"
            label="Address"
          >
            <Input.TextArea placeholder="Enter address" rows={3} />
          </Form.Item>

          <Form.Item
            name="tenantId"
            label="Tenant ID (Optional)"
          >
            <Input placeholder="tenant_id" />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button onClick={() => {
              setIsModalVisible(false);
              form.resetFields();
            }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={isAdding}>
              Add Location
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default LocationSelector;